import Vue from 'vue'

const debug = false

export default (carrier, id, options) => {
  options = {
    missing: null,
    present: data => data,
    watch: null,
    ...options
  }

  return new Vue({
    data () {
      return {
        id,
        options,
        names: {},
        watcher: null
      }
    },
    computed: {
      all () {
        return Object.fromEntries(
          Object.entries(carrier.repository.list(this.id))
            .map(([key, content]) => {
              return [key, this.options.present(content.data)]
            })
        )
      },
      objects () {
        return carrier.repository.list(this.id)
      }
    },
    methods: {
      get (path, missing) {
        return path in this.all ? this.all[path] : this.options.present(missing ? missing : this.options.missing)
      },
      find (name, missing) {
        if (name in this.names) {
          return this.get(this.names[name], missing)
        } else {
          return this.options.present(missing ? missing : this.options.missing)
        }
      },
      async add (mixed) {
        const entries = this._toEntries(mixed)
        if (entries.length === 0) return false

        if (debug) console.log('[CARRIER] Add objects', entries.map(entry => entry.path).join(', '))
        const promises = []
        for (const entry of entries) {
          if (entry.name) this.$set(this.names, entry.name, entry.path)
          if (carrier.repository.add(entry.path, this.id, this.options.missing)) {
            promises.push(new Promise(async (resolve) => {
              try {
                const result = await carrier.get(entry.path)
                carrier.repository.update(entry.path, result.data)
                resolve(this.options.present(result.data))
              } catch (e) {
                console.warn('Could not load resource ' + entry.path + ' : ' + e.message)
              }
            }))
          } else {
            promises.push(new Promise(async (resolve) => {
              resolve(this.get(entry.path))
            }))
          }
        }

        if (promises.length === 1) return promises[0]
        else return promises
      },
      remove (mixed) {
        const entries = this._toEntries(mixed)
        if (entries.length === 0) return false

        if (debug) console.log('[CARRIER] Remove objects', entries.map(entry => entry.path).join(', '))
        for (const entry of entries) {
          this.$delete(this.names, entry.name)
          carrier.repository.remove(this.id, entry.path)
        }
      },
      sync (mixed) {
        const entries = this._toEntries(mixed)
        const oldPaths = Object.keys(carrier.repository.list(this.id))

        if (debug) console.log('[CARRIER] Sync (NEW/OLD)', entries.map(entry => entry.path), oldPaths)

        this.add(entries.filter(entry => !oldPaths.includes(entry.path)))
        this.remove(oldPaths.filter(path => entries.find(entry => entry.path === path) === undefined))

        // HOTFIX FOR DOUBLE OBJECT WITH SAME NAME
        for (let entry of entries) {
          if (entry.name) this.$set(this.names, entry.name, entry.path)
        }
      },
      watch (source) {
        // If we have a previous watcher, remove it
        if (this.watcher) this.watcher()

        this.watcher = this.$watch(source, (value) => {
          this.sync(value)
        })
      },
      set (objects) {
        for (let [path, data] of Object.entries(objects)) {
          path = carrier.sanitize(path)
          carrier.repository.set(path, this.id, data)
        }
      },
      purge () {
        if (this.watcher) this.watcher()
        this.sync([])
      },
      destroy () {
        this.$destroy()
      },
      _toEntries (mixed) {
        if (!mixed) {
          return []
        } else if (Array.isArray(mixed)) {
          return mixed.map(mixed => {
            if (typeof mixed === 'string') {
              return {
                path: mixed ? '/' + mixed.replace(new RegExp('^/+'), '') : undefined,
                name: undefined
              }
            } else {
              return {
                path: mixed.path ? '/' + mixed.path.replace(new RegExp('^/+'), '') : undefined,
                name: mixed.name ? mixed.name.toString() : mixed.name
              }
            }
          }).filter(entry => entry.path)
        } else if (typeof (mixed) === 'object') {
          return Object.entries(mixed).map(([key, val]) => {
            return {
              path: val ? '/' + val.replace(new RegExp('^/+'), '') : undefined,
              name: key.toString()
            }
          }).filter(entry => entry.path)
        } else if (typeof (mixed) === 'string') {
          return mixed ? [
            {
              path: '/' + mixed.replace(new RegExp('^/+'), ''),
              name: undefined
            }
          ] : []
        }
      }
    },
    created () {
      carrier.collections[this.id] = this

      if (this.options.watch) {
        this.watch(this.options.watch)
        this.sync(this.options.watch())
      }
    },
    destroyed () {
      this.purge()
      delete carrier.collections[this.id]
    }
  })
}